import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../api/index";
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Chip,
  Skeleton,
} from "@mui/material";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { colors, boxStyle } from "../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BugReportIcon from "@mui/icons-material/BugReport";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { computeSecurityScore } from "../../network-status/status/status-components/securityScore";
import {
  mapAssetRows,
  vulnAckKey,
  vulnSeverityRank,
  vulnSeverityTier,
  severityStyleForTier,
  SEVERITY_TIER_ORDER,
  allVulnerabilitiesForAsset,
  assetIdentity,
  riskScoreStyle,
  parseRiskFactors,
  exposureScoreFor,
} from "../../network-status/status/status-components/assetUtils";
import { assetTypeLabel } from "../../network-status/status/status-components/assetClassification";

// Same reseller/user branch used by the Network Overview to open an asset's
// full detail page (status/index.jsx, overview/index.jsx) — kept in sync so a
// click here lands on the exact same page a click there would.
const ASSET_DETAIL_PATH = window.sessionStorage.getItem("type") === "reseller"
  ? "/reseller/myC/asset-detail"
  : "/user/network-status/asset-detail";

// Network Overview — the page this widget's score/breakdown are mirrored
// from (AssetsOverview.jsx's "Security" tab) — same reseller/user branch.
const NETWORK_OVERVIEW_PATH = window.sessionStorage.getItem("type") === "reseller"
  ? "/reseller/myC/overview"
  : "/user/network-status/overview";

// Maps computeSecurityScore's breakdown keys to what clicking that factor opens.
const BREAKDOWN_ICONS = { assetRisk: WarningAmberIcon, zoneRisk: DevicesOtherIcon, unackedCves: BugReportIcon };

const TREND_POINTS = 4;

/* ── Weekly trend sparkline ──────────────────────────────
 * Real chart component (@mui/x-charts, already a dependency — same package
 * AssetsOverview.jsx uses for its donut charts) instead of a hand-rolled SVG.
 * Width is intentionally omitted: SparkLineChart auto-sizes to its container
 * via ResizeObserver, so it fills whatever width the layout gives it.
 * Always shows exactly TREND_POINTS points: when there isn't enough history
 * yet, the missing leading points are padded with 0 rather than shrinking
 * the chart to fewer points. */
const TrendSparkline = ({ points, color = "#7de8ed", height = 36 }) => {
  if (!points || points.length === 0) return null;
  const trimmed = points.slice(-TREND_POINTS);
  const padding = Array(Math.max(0, TREND_POINTS - trimmed.length)).fill(0);
  const data = [...padding, ...trimmed];
  return (
    <SparkLineChart
      data={data}
      height={height}
      // "natural" (cubic spline) can overshoot BELOW zero between a run of
      // zero-padding and the first real score, drawing a visible dip under
      // the axis — "monotoneX" stays smooth/rounded between points but never
      // overshoots past them, which a spline can't guarantee.
      curve="monotoneX"
      area
      showHighlight
      color={color}
      sx={{
        "& .MuiAreaElement-root": { fill: color, opacity: 0.15 },
        "& .MuiLineElement-root": { strokeWidth: 2 },
      }}
    />
  );
};

// Week-over-week delta — also feeds the sparkline color below.
const trendDelta = (history) => {
  if (!history || history.length < 2) return null;
  const last = history[history.length - 1].score;
  const prev = history[history.length - 2].score;
  return { delta: last - prev };
};

const TREND_COLOR = { up: "#77D168", down: "#DB4F4A", flat: "#9a9a9a" };

// Nothing but the direction — no label, no percentage, no "first reading"
// filler text. Renders nothing until there's a second week to compare against.
const TrendArrow = ({ history }) => {
  const d = trendDelta(history);
  if (!d) return null;
  const dir = d.delta > 0 ? "up" : d.delta < 0 ? "down" : "flat";
  const Icon = dir === "up" ? ArrowUpwardIcon : dir === "down" ? ArrowDownwardIcon : RemoveIcon;
  return <Icon sx={{ fontSize: 18, color: TREND_COLOR[dir] }} />;
};

// Score-breakdown bars, now doubling as the widget's drill-down buttons — no
// separate row of stat tiles. Clicking a factor opens exactly the data behind
// that factor's penalty (same bars/values as the Network Overview's "Security"
// tab, AssetsOverview.jsx, just interactive here).
const BreakdownBar = ({ label, penalty, max, count, onClick }) => {
  const pct = max > 0 ? Math.min(100, Math.round((penalty / max) * 100)) : 0;
  const barColor = pct === 0 ? colors.grey[700] : pct < 40 ? "#ffb422" : pct < 75 ? "#ff7744" : "#ff4444";
  const Icon = BREAKDOWN_ICONS[label._key] || null;
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        borderRadius: 1,
        p: 1,
        mx: -1,
        transition: "background-color 0.15s ease",
        "&:hover": onClick ? { backgroundColor: "rgba(255,255,255,0.04)" } : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
          {Icon && <Icon sx={{ fontSize: 15, color: colors.grey[500], flexShrink: 0 }} />}
          <Typography sx={{ fontSize: 12, color: colors.grey[300], fontWeight: 600, whiteSpace: "nowrap" }}>{label.text}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {typeof count === "number" && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.grey[100] }}>{count}</Typography>
          )}
          {onClick && <ChevronRightIcon sx={{ fontSize: 16, color: colors.grey[600] }} />}
        </Stack>
      </Box>
      <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#161616", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: barColor, borderRadius: 3, transition: "width 0.4s ease" }} />
      </Box>
    </Box>
  );
};

// Identity + context line shown above every asset's detail in the popups —
// hostname/IP first (assetIdentity's own fallback order), then whatever's
// known about what it is (type/OS/vendor). Clicking it opens the asset's full
// detail page (same destination the Network Overview's own drill-downs use),
// so a CVE or risk factor found here is one click from the full asset record.
const AssetHeader = ({ row, exposure }) => {
  const navigate = useNavigate();
  const subtitleParts = [
    row.asset_type ? assetTypeLabel(row.asset_type) : null,
    row.os?.family || null,
    row.vendor || null,
  ].filter(Boolean);
  const exposureParts = exposure
    ? [
        typeof exposure.score === "number" ? `Exposure ${exposure.score}` : null,
        typeof exposure.connCount === "number" ? `${exposure.connCount} connection${exposure.connCount === 1 ? "" : "s"}` : null,
        exposure.hasExternalConnections ? "reaches external IPs" : null,
        typeof exposure.servicesCount === "number" && exposure.servicesCount > 0
          ? `${exposure.servicesCount} exposed service${exposure.servicesCount === 1 ? "" : "s"}`
          : null,
      ].filter(Boolean)
    : [];
  return (
    <Box
      onClick={() => navigate(`${ASSET_DETAIL_PATH}?assetId=${encodeURIComponent(row.id)}`)}
      sx={{
        mb: 1, cursor: "pointer", borderRadius: 1, p: 0.6, mx: -0.6,
        "&:hover .asset-header-name": { color: "#7de8ed", textDecoration: "underline" },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography className="asset-header-name" sx={{ fontSize: 13.5, fontWeight: 700, color: colors.grey[100] }}>
          {assetIdentity(row) || "Unknown asset"}
        </Typography>
        {row.ip && assetIdentity(row) !== row.ip && (
          <Typography sx={{ fontSize: 12, color: colors.grey[500] }}>{row.ip}</Typography>
        )}
      </Stack>
      {subtitleParts.length > 0 && (
        <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>{subtitleParts.join(" · ")}</Typography>
      )}
      {exposureParts.length > 0 && (
        <Typography sx={{ fontSize: 11.5, color: exposure.hasExternalConnections ? "#ff7744" : colors.grey[500] }}>
          {exposureParts.join(" · ")}
        </Typography>
      )}
    </Box>
  );
};

// Compact risk-score explanation — same data/visual language as RiskScoreCard
// on the full Asset Detail page (AssetDetailContent.jsx), trimmed for a popup list.
const RiskFactorsCard = ({ row }) => {
  const style = riskScoreStyle(row.risk_score);
  const parsed = useMemo(() => parseRiskFactors(row.risk_factors), [row.risk_factors]);
  const maxWeight = parsed.reduce((max, f) => Math.max(max, f.weight ?? 0), 0);
  return (
    <Box sx={{ borderRadius: 1, border: `1px solid ${colors.grey[800]}`, p: 1.2, mb: 1 }}>
      <AssetHeader row={row} />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: parsed.length ? 1 : 0 }}>
        <Box
          sx={{
            width: 54, height: 54, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `3px solid ${style.color}`, color: style.color,
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{row.risk_score}</Typography>
        </Box>
        <Chip label={style.label} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 10.5, height: 20 }} />
      </Stack>
      {parsed.length > 0 && (
        <Stack spacing={0.5}>
          {parsed.map((f, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: colors.grey[200], flex: "1 1 auto", minWidth: 0 }}>{f.label}</Typography>
              <Box sx={{ width: 80, height: 5, borderRadius: 3, backgroundColor: "#161616", overflow: "hidden", flexShrink: 0 }}>
                <Box
                  sx={{
                    height: "100%",
                    width: maxWeight > 0 && f.weight != null ? `${Math.round((f.weight / maxWeight) * 100)}%` : 0,
                    backgroundColor: style.color,
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 11.5, fontFamily: "monospace", fontWeight: 700, color: style.color, minWidth: 30, textAlign: "right", flexShrink: 0 }}>
                {f.weight != null ? `+${f.weight}` : "—"}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// Per-asset severity counts, e.g. "Critical 11 · High 2" — no per-CVE rows.
const severityCounts = (vulns) => {
  const byTier = {};
  (vulns || []).forEach((v) => {
    const tier = vulnSeverityTier(v);
    byTier[tier] = (byTier[tier] || 0) + 1;
  });
  return SEVERITY_TIER_ORDER.filter((tier) => byTier[tier]).map((tier) => ({ tier, count: byTier[tier] }));
};

// One tenant here can mean thousands of assets, each with dozens of CVEs — a
// full VulnerabilityList (description, exploits, acknowledge) per asset would
// mean rendering (and having fetched) tens of thousands of CVE rows just to
// open this popup. This is a "how many hosts are at risk, and how badly"
// summary, not a CVE browser: counts per severity, click the host for the
// full picture on its own Asset Detail page (Inventory).
const VulnerabilitiesDetail = ({ groups }) => {
  if (groups.length === 0) {
    return <Typography sx={{ color: colors.grey[600], fontSize: 13, fontStyle: "italic" }}>Nothing to show.</Typography>;
  }
  return (
    <Stack spacing={1}>
      {groups.map(({ row, vulns, exposure }) => (
        <Box key={row.id} sx={{ borderRadius: 1, border: `1px solid ${colors.grey[800]}`, p: 1.2 }}>
          <AssetHeader row={row} exposure={exposure} />
          <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            {severityCounts(vulns).map(({ tier, count }) => {
              const style = severityStyleForTier(tier);
              return (
                <Chip
                  key={tier}
                  label={`${style.label} ${count}`}
                  size="small"
                  sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 10.5, height: 20 }}
                />
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const HighRiskAssetsDetail = ({ rows }) => {
  if (rows.length === 0) {
    return <Typography sx={{ color: colors.grey[600], fontSize: 13, fontStyle: "italic" }}>Nothing to show.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => <RiskFactorsCard key={row.id} row={row} />)}
    </Stack>
  );
};

// Each zone opens Network Zones (where risk level, notes and subnets/VLANs are
// actually managed) instead of just displaying the numbers read-only.
const ZoneRiskDetail = ({ zones }) => {
  const navigate = useNavigate();
  if (zones.length === 0) {
    return <Typography sx={{ color: colors.grey[600], fontSize: 13, fontStyle: "italic" }}>Nothing to show.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {zones.map((z) => {
        const style = z.risk === "critical" ? { color: "#ff4444", bg: "#ff222233" } : { color: "#ff7744", bg: "#ff552233" };
        return (
          <Box
            key={z.id}
            onClick={() => navigate("/user/network-status/zones")}
            sx={{
              borderRadius: 1, border: `1px solid ${colors.grey[800]}`, p: 1.5, cursor: "pointer",
              transition: "border-color 0.15s ease",
              "&:hover": { borderColor: "#459095" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.grey[100] }}>{z.name}</Typography>
              <Chip label={z.risk} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 10.5, height: 20, textTransform: "capitalize" }} />
              <ChevronRightIcon sx={{ color: colors.grey[600], fontSize: 18, ml: "auto" }} />
            </Stack>
            {z.note && <Typography sx={{ fontSize: 12.5, color: colors.grey[300], mb: 0.8 }}>{z.note}</Typography>}
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>{z.assetCount} asset{z.assetCount === 1 ? "" : "s"} in this zone</Typography>
              {typeof z.purdue_level === "number" && (
                <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>Purdue level {z.purdue_level}</Typography>
              )}
              {typeof z.target_sl === "number" && (
                <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>Target SL {z.target_sl}</Typography>
              )}
              {z.subnets?.length > 0 && (
                <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>{z.subnets.join(", ")}</Typography>
              )}
              {z.vlans?.length > 0 && (
                <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>VLAN {z.vlans.join(", ")}</Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

// Same shape as the widget's own `loading` state below — exported so the parent
// dashboard can render it in the gap while it's still resolving *whether* this
// widget (vs. the legacy RatingGraph) even applies, instead of leaving that grid
// cell empty and creating a visible hole next to the other cards' skeletons.
export const OrionRatingGraphSkeleton = () => (
  <Box sx={{ ...boxStyle, p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Network Security Score</Typography>
    </Stack>
    <Stack spacing={1.5} sx={{ flex: 1 }}>
      <Skeleton variant="rounded" height={130} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
      {[...Array(3)].map((_, i) => (
        <Skeleton key={i} variant="rounded" height={28} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
      ))}
    </Stack>
  </Box>
);

/* ── Main Component ───────────────────────────────────── */
// Fully independent from the legacy RatingGraph (passive-vulnerabilities widget) —
// rendered instead of it by the parent dashboard when Orion is active. Reuses
// computeSecurityScore (same scoring as the Network Overview "Security" tab) and
// the same asset/zone/CVE data, so the two views never disagree.
function OrionRatingGraph({ setVisibleGlobe }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orionRows, setOrionRows] = useState([]);
  const [orionZones, setOrionZones] = useState([]);
  const [orionAcks, setOrionAcks] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [exposureByIp, setExposureByIp] = useState({});

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogKey, setDialogKey] = useState(null);

  // Acknowledged CVEs are excluded from the score/breakdown here (see
  // vulnGroups below), but acknowledging/reopening one is done from the
  // asset's own detail page (Inventory), not from this summary popup.
  const ackSet = useMemo(
    () => new Set(orionAcks.map((a) => vulnAckKey(a.asset_id, a.cve_id, a.origin))),
    [orionAcks]
  );

  const orionScore = useMemo(
    () => (loading ? null : computeSecurityScore({ rows: orionRows, zones: orionZones, acks: orionAcks })),
    [loading, orionRows, orionZones, orionAcks]
  );

  useEffect(() => {
    Promise.all([
      API.getRecentHost({ serials: JSON.stringify([]), all: true, orion: true }),
      API.getZones(),
      API.getCveAcks(),
    ])
      .then(([hostRes, zonesRes, acksRes]) => {
        const hostData = hostRes.data || hostRes;
        const rows = hostData?.success ? mapAssetRows(hostData.data) : [];
        const zones = zonesRes?.data?.success ? zonesRes.data.data || [] : [];
        const acks = acksRes?.data?.success ? acksRes.data.data || [] : [];
        setOrionRows(rows);
        setOrionZones(zones);
        setOrionAcks(acks);
      })
      .catch((err) => console.error("[OrionRatingGraph] Errore nel recupero dei dati Orion:", err))
      .finally(() => setLoading(false));
  }, []);

  // Network exposure (connection count + whether any connection reaches an
  // external IP) — one bulk aggregate for the whole tenant, used only as a
  // tie-breaker between CVEs of equal severity (see vulnGroups below), never
  // for the score itself. Independent of the main fetch above, so a slow/failed
  // exposure query never blocks the score from rendering.
  useEffect(() => {
    API.getAssetExposure()
      .then((res) => setExposureByIp(res?.data?.success ? res.data.data || {} : {}))
      .catch((err) => console.error("[OrionRatingGraph] Errore nel recupero dell'esposizione di rete:", err));
  }, []);

  useEffect(() => {
    if (orionScore?.score > 0) setVisibleGlobe(true);
  }, [orionScore, setVisibleGlobe]);

  // Weekly trend — record this week's snapshot (upsert) and fetch the history
  // used for the trend arrow and sparkline.
  useEffect(() => {
    if (!orionScore) return;
    API.recordSecurityScoreHistory({ score: orionScore.score })
      .then(() => API.getSecurityScoreHistory())
      .then((res) => setScoreHistory(res?.data?.success ? res.data.data || [] : []))
      .catch((err) => console.error("[OrionRatingGraph] Errore nel recupero dello storico dello score:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orionScore?.score]);

  // Assets whose worst CVE severity ties get ordered by how exposed they
  // actually are (exposureScoreFor in assetUtils.js — external reachability
  // first, then exposed-service count, then raw connection volume). A
  // heuristic tie-breaker only: it never changes which severity sorts first,
  // only the order among assets that tie on severity.
  const vulnGroups = useMemo(() => {
    return orionRows
      .map((row) => {
        const vulns = allVulnerabilitiesForAsset(row).filter(
          (v) => !ackSet.has(vulnAckKey(row.id, v.cve_id, v._originKey))
        );
        const exp = exposureByIp[row.ip];
        const exposure = exp || row.services?.length
          ? { score: exposureScoreFor(row, exposureByIp), connCount: exp?.connCount, hasExternalConnections: exp?.hasExternalConnections, servicesCount: row.services?.length || 0 }
          : null;
        return { row, vulns, exposure, worstRank: Math.max(0, ...vulns.map(vulnSeverityRank)) };
      })
      .filter((g) => g.vulns.length > 0)
      .sort((a, b) => b.worstRank - a.worstRank || exposureScoreFor(b.row, exposureByIp) - exposureScoreFor(a.row, exposureByIp));
  }, [orionRows, ackSet, exposureByIp]);

  const highRiskRows = useMemo(
    () => orionRows.filter((r) => typeof r.risk_score === "number" && r.risk_score >= 60),
    [orionRows]
  );

  const riskyZones = useMemo(() => {
    const counts = {};
    orionRows.forEach((r) => { if (r.zone_id) counts[r.zone_id] = (counts[r.zone_id] || 0) + 1; });
    return orionZones
      .filter((z) => z.risk === "critical" || z.risk === "high")
      .map((z) => ({ ...z, id: z._id ?? z.id, assetCount: counts[z._id ?? z.id] || 0 }));
  }, [orionRows, orionZones]);

  const openDetail = (key, title) => {
    setDialogKey(key);
    setDialogTitle(title);
    setOpenDialog(true);
  };
  const closeDetail = () => setOpenDialog(false);

  // What each breakdown bar opens/shows, keyed the same as computeSecurityScore's
  // `breakdown` array — bridges the generic scoring output to this widget's data.
  const breakdownActions = {
    assetRisk: { count: highRiskRows.length, onClick: () => openDetail("highRisk", "High-Risk Assets") },
    zoneRisk: { count: riskyZones.length, onClick: () => openDetail("zoneRisk", "Zone Risk") },
    unackedCves: { count: vulnGroups.reduce((sum, g) => sum + g.vulns.length, 0), onClick: () => openDetail("vulnerabilities", "Unacknowledged CVEs") },
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none", height: "75vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            borderBottom: `1px solid ${colors.grey[800]}`,
            pb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>{dialogTitle}</Typography>
          <IconButton onClick={closeDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
          {dialogKey === "vulnerabilities" && (
            <VulnerabilitiesDetail groups={vulnGroups} />
          )}
          {dialogKey === "highRisk" && <HighRiskAssetsDetail rows={highRiskRows} />}
          {dialogKey === "zoneRisk" && <ZoneRiskDetail zones={riskyZones} />}
        </DialogContent>
      </Dialog>

      <Box sx={{ ...boxStyle, p: 2, height: "100%", display: "flex", flexDirection: "column" }} className="orionRatingGraph">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Network Security Score</Typography>
          <Button
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => navigate(NETWORK_OVERVIEW_PATH)}
            sx={{ color: "#459095", textTransform: "none", fontSize: 11.5 }}
          >
            Network Overview
          </Button>
        </Stack>

        {loading ? (
          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Skeleton variant="rounded" height={130} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={28} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
            ))}
          </Stack>
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Score + trend — the two things this widget leads with */}
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box
                  sx={{
                    width: 124, height: 124, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `5px solid ${orionScore?.color}`, color: orionScore?.color,
                  }}
                >
                  <Typography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>{orionScore?.score}</Typography>
                </Box>
              </Box>
              <Box sx={{ width: "50%", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.3, height: 18 }}>
                  <TrendArrow history={scoreHistory} />
                </Box>
                <Box sx={{ width: "100%", maxWidth: 200 }}>
                  <TrendSparkline
                    points={scoreHistory.map((h) => h.score)}
                    height={36}
                    color={(() => {
                      const d = trendDelta(scoreHistory);
                      return d ? TREND_COLOR[d.delta > 0 ? "up" : d.delta < 0 ? "down" : "flat"] : TREND_COLOR.flat;
                    })()}
                  />
                </Box>
              </Box>
            </Stack>

            {orionScore?.reasons?.length > 0 && (
              <Stack spacing={0.3} sx={{ mb: 2 }}>
                {orionScore.reasons.map((r, i) => (
                  <Typography key={i} sx={{ fontSize: 12, color: colors.grey[400] }}>• {r}</Typography>
                ))}
              </Stack>
            )}

            {/* Breakdown bars double as the drill-down entry points */}
            <Stack spacing={1.2}>
              {(orionScore?.breakdown || []).map((b) => (
                <BreakdownBar
                  key={b.key}
                  label={{ text: b.label, _key: b.key }}
                  penalty={b.penalty}
                  max={b.max}
                  count={breakdownActions[b.key]?.count}
                  onClick={breakdownActions[b.key]?.onClick}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}

export default OrionRatingGraph;
